import React, {useState, useEffect, FunctionComponent} from "react"
import {useNavigate, useParams} from "react-router-dom"
import {useQueryClient} from "@tanstack/react-query"
import dayjs from "dayjs"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import MBSResponseTable from "./MBSResponseTable"
import {getConvertedDate} from "@murphy-frontend/common/services/TimeService"
import {useGetSendOutResponses} from "../../../common/api/SendOutResponses/queries"
import {useUserPreferences} from "../../../common/contexts/UserPreferencesContext"
import {useModal} from "@murphy-frontend/web-core/contexts/ModalContext"
import {ModalConfiguration} from "@murphy-frontend/web-core/components/Modal"
import {useHub} from "../../../common/hooks/useHub"
import {createConnectionSendOut} from "../../../common/services/signalrService"
import {useClientMethod} from "../../../common/hooks/useClientMethod"
import queryKeys from "../../../common/api/SendOutResponses/queryKeys"
import {LogService} from "../../../common/services/LogService"
import {HubConnection, HubConnectionState} from "@microsoft/signalr"
import ComponentWrapper from "../../../common/components/ComponentWrapper"
import { RiArrowDropDownLine, RiArrowDropUpLine, RiArrowLeftLine } from "react-icons/ri"
import { CustomCheckbox } from "../../../common/components/MurphyCheckbox";
import Sms from "@murphy-frontend/web-core/assets/icons/sms.svg";
import Notification from "@murphy-frontend/web-core/assets/icons/noti.svg";
import Email from "@murphy-frontend/web-core/assets/icons/email.svg";
import { MdArrowBackIosNew } from "react-icons/md"

const styles = {
	subContainer: {
	  borderWidh: 1,
	  borderRadius: 10,
	  borderStyle: 'solid',
	  borderColor: '#EBEBEB',
	  backgroundColor: 'white',
	  width: '80rem',
	  display: 'flex',
	  flexDirection: "column",
	  marginBottom: 10,
	  alignItems: 'center'
	},
	buttonContainer: {
	  borderWidth: 1,
	  borderStyle: 'solid',
	  borderColor: '#EBEBEB',
	  width: '15rem',
	  marginLeft: 10,
	  padding: 10,
	  display: 'flex',
	  alignItems: 'center'
	},
	backContainer: {
		borderWidth: 1,
		borderStyle: 'solid',
		borderColor: '#EBEBEB',
		backgroundColor: 'white',
		justifyContent: 'space-between',
		width: '5rem',
		marginBottom: 20,
		padding: 10,
		display: 'flex',
		alignItems: 'center'
	},
	recipientContainer: {
	  borderWidh: 1,
	  borderStyle: 'solid',
	  borderColor: '#EBEBEB',
	  justifyContent: 'space-between',
	  width: '20rem',
	  padding: 10,
	  display: 'flex',
	  marginBottom: 10,
	  marginRight: 10,
	  alignItems: 'center'
	},
	container: {
	  paddingLeft: 20,
	  paddingRight: 20,
	  width: '90rem',
	  display: 'flex',
	  flexDirection: 'column'
	}
};

const MBSItem: FunctionComponent = () => {
	const {id, username} = useParams();
	const {translations, timeZone} = useUserPreferences()
	const {openModal} = useModal()
    const navigate = useNavigate();
	const [error, setError] = useState(null);
	const [isExpanded, setIsExpanded] = useState(false);
	const [isLoading, setIsLoading] = useState(true)
	const [sendOutBatch, setSendOutBatch] = useState(null)
	const [responses, setResponses] = useState([])
	const [message, setMessage] = useState({})
	const [alternatives, setAlternatives] = useState({})
	const queryClient = useQueryClient()
	const [hubConnection, setHubConnection] = useState<null | HubConnection | undefined>(null)

	const {
		data: sendOutResponsesData,
		isLoading: sendOutResponsesIsLoading,
		isError: sendOutResponsesIsError,
		error: sendOutResponsesError
	} = useGetSendOutResponses(id)

	useEffect(() => {
		const signalRConnection = createConnectionSendOut(id)
		setHubConnection(signalRConnection)
	}, [])

	const {hubConnectionState, error: signalRError} = useHub(hubConnection)

	useClientMethod(hubConnection, id, payload => {
		queryClient.setQueriesData(
			{
				queryKey: queryKeys.list(id)
			},
			(oldData: any) => {
				const update = (record: any) => {
					const {sendOutResponseAnswers} = oldData
					const newAnswers = sendOutResponseAnswers.map(ans => {
						if (ans.SendOutResponseId === record.SendOutResponseId) {
							return {
								...ans,
								responseAnswer: record.responseAnswer,
								responseSelectedAlternative: record.responseSelectedAlternative,
								dateansweredUtc: record.dateansweredUtc
							}
						} else return ans
					})
					return {
						...oldData,
						sendOutResponseAnswers: newAnswers
					}
				}
				return update(payload)
			}
		)
	})

	useEffect(() => {
		const loading = hubConnectionState !== HubConnectionState.Connected || sendOutResponsesIsLoading
		setIsLoading(loading)
	}, [hubConnectionState, sendOutResponsesIsLoading])

	useEffect(() => {
		if (sendOutResponsesIsError) {
			console.log(sendOutResponsesIsError)
			throw new Error("SendOutResp error")
		}
		if (signalRError) {
			console.log(signalRError)
			const logService = new LogService()

			let errorMessage
			if (typeof signalRError === "object") {
				errorMessage = JSON.stringify(signalRError)
			} else if (typeof signalRError === "string") {
				errorMessage = signalRError
			} else {
				errorMessage = "Unexpected type of error"
			}

			const logError = new Error("SignalR error")
			logError.name = "SignalR error"
			logError.message = errorMessage

			logService.error(logError)
			throw logError
		}
	}, [sendOutResponsesIsError, signalRError])

	const onGetSendOutResponsesBySendOutBatchIdSuccess = data => {
		const mappedResponses = data.sendOutResponseAnswers.map(p => ({
			id: p.SendOutResponseId ? p.SendOutResponseId : Math.random().toString(36).substr(2, 5),
			dateAnswered: p.dateansweredUtc ? dayjs.utc(p.dateansweredUtc) : null,
			responseAnswer: p.responseAnswer,
			responseSelectedAlternative: p.responseSelectedAlternative,
			status: p.status,
			name: p.name
		}))
		mappedResponses.sort((a, b) => b.dateAnswered - a.dateAnswered)
		const newMappedData = mappedResponses.map(p => ({
			id: p.id,
			originalDate: p.dateAnswered,
			dateAnswered: p.dateAnswered ? getConvertedDate(p.dateAnswered, timeZone) : null,
			responseAnswer: p.responseAnswer,
			responseSelectedAlternative: p.responseSelectedAlternative,
			status: p.status,
			userName: p.name
		}))
		setResponses(newMappedData)

		if (data.alternativ) {
			setAlternatives(data.alternativ)
		}

		if (data.message) {
			setMessage(data.message)
		}

		const mappedData = {
			originalDate: dayjs.utc(data.CreatedTimeUtc),
			date: getConvertedDate(dayjs.utc(data.CreatedTimeUtc), timeZone),
			status: data.status
		}
		setSendOutBatch(mappedData)

		setIsLoading(false)
	}

	const ErrorMessage = () => <p>{error}</p>

	useEffect(() => {
		if (error) {
			const modalConf: ModalConfiguration = {
				title: "Error",
				body: <ErrorMessage />,
				buttonText: "Ok",
				type: "error",
				okCallback: () => {
					setError("")
				}
			}
			openModal(modalConf)
		}
	}, [error])

	useEffect(() => {
		if (sendOutResponsesIsError) {
			setError(sendOutResponsesError)
		}
		return () => {
			setError("")
		}
	}, [sendOutResponsesIsError, sendOutResponsesError])

	useEffect(() => {
		const mappedData = responses.map(p => ({
			...p,
			dateAnswered: p.originalDate ? getConvertedDate(p.originalDate, timeZone) : null
		}))
		setResponses(mappedData)
		if (sendOutBatch) {
			const mappedSendOutBatch = {
				...sendOutBatch,
				date: sendOutBatch.originalDate ? getConvertedDate(sendOutBatch.originalDate, timeZone) : null
			}
			setSendOutBatch(mappedSendOutBatch)
		}
	}, [timeZone])

	useEffect(() => {
		if (!sendOutResponsesIsLoading) {
			onGetSendOutResponsesBySendOutBatchIdSuccess(sendOutResponsesData)
		}
	}, [sendOutResponsesIsLoading, sendOutResponsesData])

	if (isLoading) {
		return (
			<ComponentWrapper active={2} subMenuActive={2}>
				<div className='spinner-container-global-center'>
					<Spinner />
				</div>
			</ComponentWrapper>
		)
	}

	  const channels = [
		{
		  key: 1,
		  label: translations.sms || "SMS",
		  icon: <Sms />,
		  isSelected: true,
		},
		{
		  key: 2,
		  label: translations.notification || "Notification",
		  icon: <Notification />,
		  isSelected: true,
		},
		{
		  key: 3,
		  label: translations.email || "Email",
		  icon: <Email />,
		  isSelected: true,
		},
	  ];
	
	  const renderChannels = () => {
		return <div style={{ display: 'flex', width: '45rem' }}>
		  {channels.map(channel => channel.isSelected && <div style={styles.buttonContainer}>
			{channel.icon}
			<label style={{ marginLeft: 10 }}>{channel.label}</label>
		  </div>)}
		</div>
	  }

	const line = <div style={{ height: 1, backgroundColor: '#EBEBEB', width: '100%' }} />;

	return (
		<ComponentWrapper active={2} subMenuActive={2}>
			  <div style={styles.container}>
				  <div style={styles.backContainer} onClick={() => navigate(-1)}>
					 <MdArrowBackIosNew />
					 <label>{translations.back || "Back"}</label>
				  </div>
				  <label style={{fontSize: 18, fontWeight: 'bold', marginBottom: 10}}>{message.Title}</label>
				  <div style={styles.subContainer}>
					{/* Date and creator */}
					<div style={{ display: 'flex', paddingTop: 10, paddingBottom: 10 }}>
					  <div style={{ display: 'flex', width: '30rem', paddingRight: 10, justifyContent: 'space-between', alignItems: 'center' }}>
						<label style={{color: '#626262'}}>{translations.datecreate || "Date Created"}</label>
						<label style={{ width: '13rem' }}>{sendOutBatch.date}</label>
					  </div>
					  <div style={{ height: '2rem', backgroundColor: '#EBEBEB', width: 1 }} />
					  <div style={{ display: 'flex', width: '30rem', paddingLeft: 10, justifyContent: 'space-between', alignItems: 'center' }}>
						<label style={{color: '#626262'}}>{translations.creator || "Creator"}</label>
						<label style={{ width: '13rem' }}>{username}</label>
					  </div>
					</div>

					{line}

					{/* Type and critical alert */}
					<div style={{ display: 'flex', paddingTop: 10, paddingBottom: 10 }}>
					  <div style={{ display: 'flex', width: '30rem', paddingRight: 10, justifyContent: 'space-between', alignItems: 'center' }}>
						<label style={{color: '#626262'}}>{translations.typ || "Type"}</label>
						<label style={{ width: '13rem' }}>{message.Question ? translations.twoway || "2-way" : translations.oneway || "1-way"}</label>
					  </div>
					  <div style={{ height: '2rem', backgroundColor: '#EBEBEB', width: 1 }} />
					  <div style={{ display: 'flex', width: '30rem', paddingLeft: 10, justifyContent: 'space-between', alignItems: 'center' }}>
						<label style={{color: '#626262'}}>{translations.criticalAlert || "Critical Alert"}</label>
						<CustomCheckbox onChange={(val) => { }} checked={message.criticalAlert} checkedColor='#00A098' unCheckedColor='#7F7F7F' />
					  </div>
					</div>
			
					{line}
			
					{/* Channels */}
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, paddingBottom: 10, width: '60rem' }}>
					  <label style={{color: '#626262'}}>{translations.channels || "Channels"}</label>
					  {renderChannels()}
					</div>
			
					{line}
			
					{/* Title */}
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, paddingBottom: 10, width: '60rem' }}>
					  <label style={{color: '#626262'}}>{translations.title}</label>
					  <label style={{ width: '44rem' }}>{message.Title}</label>
					</div>
			
					{line}
			
					{/* Message */}
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, paddingBottom: 10, width: '60rem' }}>
					  <label style={{color: '#626262'}}>{translations.message}</label>
					  <label style={{ width: '44rem' }}>{message.Body}</label>
					</div>
			
					{message.Question && line}
			
					{/* Question */}
					{message.Question && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, paddingBottom: 10, width: '60rem' }}>
					  <label style={{color: '#626262'}}>{translations.question}</label>
					  <label style={{ width: '44rem' }}>{message.Question}</label>
					</div>
					}
					{line}
					{/* Recipients */}
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, paddingBottom: 10, width: '60rem' }}>
					  <label style={{color: '#626262'}}>{translations.recipients || "Recipients"}</label>
					  <div style={{ width: '44rem', display: 'flex', flexDirection: 'column' }}>
						<div style={{ display: 'flex', alignItems: 'center' }}>
						  <label style={{ marginRight: 5 }}>{translations.recipientslist || "Custom recipient list"} ({responses?.length})</label>
			
						  {!isExpanded ? <RiArrowDropDownLine title="Info" onClick={() => setIsExpanded(true)} size={30} /> : <RiArrowDropUpLine title="Info" onClick={() => setIsExpanded(false)} size={30} />}
						</div>
						{isExpanded && <div style={{ width: '44rem', display: 'flex', flexWrap: 'wrap', maxHeight: '15rem', overflowY: 'auto' }}>
						  {responses.map(item => {
							return <div style={styles.recipientContainer}>
							  <div style={{ display: 'flex' }}>
								<CustomCheckbox onChange={(val) => {
			
								}} checked={true} checkedColor='#00A098' />
								<label style={{ marginLeft: 10, width: '12rem', overflow: 'hidden', textOverflow: "ellipsis" }}>{item.userName}</label>
							  </div>
							  <div style={{ display: 'flex' }}>
								{item.mobileChecked && <Sms />}
								{item.emailChecked && <Email />}
								{item.notificationChecked && <Notification />}
							  </div>
							</div>
						  })}
						</div>}
					  </div>
			
					</div>
				  </div>

				  <label style={{marginTop: 20, marginBottom: 10, color: '#7F7F7F'}}>{translations.responses || "Responses"}</label>
				 <div style={{width: '80rem'}}>
				 <MBSResponseTable translations={translations} rows={responses} />
				
				 </div>
				
				</div>
		</ComponentWrapper>
	)
}

export default MBSItem
